// Client-side SEO head lifecycle. The backend (GET /api/seo/head, backend/seo.py) is the single
// source of truth for title, description, canonical, robots, hreflang, OpenGraph and JSON-LD: the
// Netlify edge function injects it into the HTML of a direct load, and the SPA applies the same
// payload after every client-side navigation or language switch, so the head always describes the
// current URL (no stale BreadcrumbList/hreflang/Product data from the previous page).

// Tags owned by the SEO payload; everything else in <head> (analytics, manifest, fonts) is left alone.
const MANAGED = [
  'meta[name="description"]', 'link[rel="canonical"]', 'meta[name="robots"]', 'link[rel="alternate"][hreflang]',
  'script[type="application/ld+json"]', 'meta[property^="og:"]', 'meta[property^="product:"]',
  'meta[property^="article:"]', 'meta[name^="twitter:"]',
].join(',');

const cache = new Map();
const CACHE_MS = 120000;

export function seoHeadUrl(pathname, lang) {
  const path = String(pathname || '/').replace(/\/+$/, '') || '/';
  return `/api/seo/head?${new URLSearchParams({path, lang: lang === 'ru' ? 'ru' : 'uk'})}`;
}

/** Resolves with the payload (including real 404/410 answers); rejects on network/5xx errors. */
export function fetchSeoHead(pathname, lang, fetcher = globalThis.fetch) {
  const url = seoHeadUrl(pathname, lang);
  const hit = cache.get(url);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise;
  const promise = fetcher(url, {credentials: 'omit'}).then(response => {
    if (!response.ok) throw new Error(`seo head ${response.status}`);
    return response.json();
  }).then(data => {
    if (!data || typeof data.title !== 'string') throw new Error('invalid seo head');
    return data;
  });
  cache.set(url, {at: Date.now(), promise});
  promise.catch(() => cache.delete(url));
  return promise;
}

function el(doc, tag, attrs) {
  const node = doc.createElement(tag);
  Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
  return node;
}

export function applySeoHead(doc, data) {
  if (!data || typeof data.title !== 'string') return;
  const head = doc.head;
  head.querySelectorAll(MANAGED).forEach(node => node.remove());
  doc.title = data.title;
  const nodes = [el(doc, 'meta', {name: 'description', content: data.description || ''})];
  if (data.canonical) nodes.push(el(doc, 'link', {rel: 'canonical', href: data.canonical}));
  const metas = Array.isArray(data.meta) ? data.meta : [];
  if (!metas.some(m => m.name === 'robots') && data.robots) nodes.push(el(doc, 'meta', {name: 'robots', content: data.robots}));
  metas.forEach(meta => {
    const key = meta.property ? 'property' : 'name';
    if (meta[key]) nodes.push(el(doc, 'meta', {[key]: meta[key], content: meta.content ?? ''}));
  });
  (Array.isArray(data.alternates) ? data.alternates : []).forEach(alt => nodes.push(el(doc, 'link', {rel: 'alternate', hreflang: alt.hreflang, href: alt.href})));
  (Array.isArray(data.jsonld) ? data.jsonld : []).forEach(item => {
    const script = doc.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(item).replace(/</g, '\\u003c');
    nodes.push(script);
  });
  nodes.forEach(node => head.appendChild(node));
}

export const _test = {reset: () => cache.clear()};
